import { Scene } from '../Scene';
import { SceneManifest } from '../SceneManifest';
import { Gltf, GltfParser, ResolvedMesh, resolveMaterialTextures } from '../../gltf';
import { ImageTexture, Texture } from '../../texture';
import { Material } from '../../material';
import { SceneLoadProgressTracker } from '../SceneLoadProgress';
import { inflateGzip } from '../../util/gzip';

export type ParsedModel = {
  parser: GltfParser;
  meshes: ResolvedMesh[];
  rawMeshes: ResolvedMesh[];
  meshByName: Map<string, ResolvedMesh>;
  rawMeshByName: Map<string, ResolvedMesh>;
  materials: Map<number, Material>;
  lightmapTextures: Texture[];
  json: Gltf;
  binaries: Uint8Array[];
};

export async function loadModels(
  scene: Scene,
  manifest: SceneManifest,
  tracker?: SceneLoadProgressTracker,
): Promise<Map<string, ParsedModel>> {
  const modelCache = new Map<string, ParsedModel>();
  const entries = Object.entries(manifest.assets.models);
  let completed = 0;

  // Fetch all GLB bytes in parallel, then process each model independently. The
  // GL-touching work inside each model (`resolveMaterialTextures`,
  // `ImageTexture.load`) still runs as one promise chain — interleaving across
  // models is fine because none of those calls hold GL state across awaits.
  await Promise.all(
    entries.map(async ([key, modelDef]) => {
      const compressed = await scene.ctx.resourceLoader.loadResource(modelDef.path);
      const glbData = await inflateGzip(compressed);
      const parser = new GltfParser(glbData);
      const parsed = parser.parse();

      const resolvedTextures = parser.resolveTextures(parsed.json, parsed.binaries);
      const materials = await resolveMaterialTextures(scene.ctx, resolvedTextures);

      const lightmapTextures: Texture[] = [];
      if (modelDef.lightmapPages) {
        const pages = await Promise.all(
          modelDef.lightmapPages.map(async (pagePath) => {
            const pageImg = new ImageTexture({ source: pagePath });
            await pageImg.load(scene.ctx);
            return pageImg;
          }),
        );
        lightmapTextures.push(...pages);
      }

      // resolveSceneMeshes bakes the glTF node-hierarchy transforms (translation/rotation/scale)
      // into vertex positions. This is needed for static models (e.g. Sponza) where sub-meshes
      // are positioned by their parent nodes. resolveAllMeshes returns raw vertices without
      // node transforms — needed for animated/skinned models where the skinning shader already
      // applies joint matrices derived from the same node hierarchy; baking them into vertices
      // would double-apply the transform and deform the mesh.
      const meshes = parser.resolveSceneMeshes(parsed.json, parsed.binaries);
      const rawMeshes = parser.resolveAllMeshes(parsed.json, parsed.binaries);
      const meshByName = new Map(meshes.map((m) => [m.name, m]));
      const rawMeshByName = new Map(rawMeshes.map((m) => [m.name, m]));
      modelCache.set(key, {
        parser,
        meshes,
        rawMeshes,
        meshByName,
        rawMeshByName,
        materials,
        lightmapTextures,
        json: parsed.json,
        binaries: parsed.binaries,
      });
      completed++;
      tracker?.updateStageProgress(completed / Math.max(1, entries.length), key);
    }),
  );
  return modelCache;
}
